import { buildGs1DigitalLinkUrl } from '@myplayer/gs1';
import { isIdentifierRecalled, type IdentifierRecord } from '@myplayer/schema';
import jsQR, { type QRCode } from 'jsqr';
export { renderQrCodeTestFrame } from './qr-check-test-frame.js';

export const QR_CHECK_VERIFIER_NOTICE =
  'Decodes and resolves. Print grade needs an ISO/IEC 15415 verifier.';

export function openQrCheckInNewTab(productIdentifierKey?: string | null): string {
  const path =
    productIdentifierKey !== undefined &&
    productIdentifierKey !== null &&
    productIdentifierKey.trim().length > 0
      ? `/qr-check?product=${encodeURIComponent(productIdentifierKey.trim())}`
      : '/qr-check';
  if (typeof window !== 'undefined' && typeof window.open === 'function') {
    window.open(path, '_blank', 'noopener,noreferrer');
  }
  return path;
}

export type QrRoughWarningCode = 'MISSING_QUIET_ZONE' | 'TINY_SIZE' | 'LOW_CONTRAST';
export interface QrRoughWarning {
  readonly code: QrRoughWarningCode;
  readonly label: string;
  readonly message: string;
}
export interface QrBoundingBox {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
  readonly widthPx: number;
  readonly heightPx: number;
}
export interface QrFrameAnalysis {
  readonly decoded: boolean;
  readonly decodedLink: string | null;
  readonly version: number | null;
  readonly boundingBox: QrBoundingBox | null;
  readonly sizeRatio: number | null;
  readonly contrastRatio: number | null;
  readonly quietZoneClear: boolean | null;
  readonly warnings: readonly QrRoughWarning[];
}
export interface QrLiveResolutionCheckResult {
  readonly resolvesToRightLiveLink: boolean;
  readonly expectedLiveUrl: string | null;
  readonly resolvedLiveUrl: string | null;
  readonly source: 'http' | 'record';
  readonly summary: string;
}
export interface QrCheckEvaluation {
  readonly frameAnalysis: QrFrameAnalysis;
  readonly matchedProduct: IdentifierRecord | null;
  readonly expectedProductLink: string | null;
  readonly matchesExpectedLink: boolean;
  readonly expectedLinkSummary: string;
  readonly liveResolution: QrLiveResolutionCheckResult;
  readonly verifierNotice: typeof QR_CHECK_VERIFIER_NOTICE;
}
export type LiveLinkResolverProbe = (params: {
  readonly decodedLink: string;
  readonly matchedProduct: IdentifierRecord | null;
  readonly expectedLiveUrl: string | null;
}) => Promise<{ readonly resolvedUrl: string | null; readonly ok: boolean }>;

const ROUGH_WARNING_METADATA: Readonly<Record<QrRoughWarningCode, QrRoughWarning>> = {
  MISSING_QUIET_ZONE: {
    code: 'MISSING_QUIET_ZONE',
    label: 'Missing quiet zone',
    message:
      'The light border around the QR code looks cramped or has dark marks too close to the edge. Leave a clear 4-module margin on all sides.',
  },
  TINY_SIZE: {
    code: 'TINY_SIZE',
    label: 'Tiny size',
    message:
      'The QR code looks very small in the frame. Move the camera closer or check that the printed symbol is large enough to scan easily.',
  },
  LOW_CONTRAST: {
    code: 'LOW_CONTRAST',
    label: 'Low contrast',
    message:
      'The difference between dark dots and the light background looks low. Print dark dots on a bright, non-glossy light background.',
  },
};

type JsQrCallable = (
  data: Uint8ClampedArray,
  width: number,
  height: number,
  options?: { readonly inversionAttempts?: 'attemptBoth' },
) => QRCode | null;

function runJsQrFull(rgba: Uint8ClampedArray, width: number, height: number): QRCode | null {
  const mod = jsQR as unknown as { readonly default?: JsQrCallable } & JsQrCallable;
  const fn: JsQrCallable = typeof mod.default === 'function' ? mod.default : mod;
  return fn(rgba, width, height, { inversionAttempts: 'attemptBoth' });
}

function srgbToRelativeLuminance(r: number, g: number, b: number): number {
  const lin = (c: number): number => {
    const n = c / 255;
    return n <= 0.03928 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function computeBoundingBox(qr: QRCode): QrBoundingBox {
  const { topLeftCorner, topRightCorner, bottomLeftCorner, bottomRightCorner } = qr.location;
  const xs = [topLeftCorner.x, topRightCorner.x, bottomLeftCorner.x, bottomRightCorner.x];
  const ys = [topLeftCorner.y, topRightCorner.y, bottomLeftCorner.y, bottomRightCorner.y];
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    minX,
    minY,
    maxX,
    maxY,
    widthPx: Math.max(1, maxX - minX),
    heightPx: Math.max(1, maxY - minY),
  };
}

function measureBoxContrast(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  box: QrBoundingBox,
): { readonly contrastRatio: number; readonly darkLum: number; readonly lightLum: number } {
  const x0 = Math.max(0, Math.floor(box.minX));
  const y0 = Math.max(0, Math.floor(box.minY));
  const x1 = Math.min(width - 1, Math.ceil(box.maxX));
  const y1 = Math.min(height - 1, Math.ceil(box.maxY));
  const stepX = Math.max(1, Math.floor((x1 - x0) / 32));
  const stepY = Math.max(1, Math.floor((y1 - y0) / 32));
  const samples: number[] = [];
  for (let y = y0; y <= y1; y += stepY) {
    for (let x = x0; x <= x1; x += stepX) {
      const idx = (y * width + x) * 4;
      samples.push(srgbToRelativeLuminance(rgba[idx] ?? 0, rgba[idx + 1] ?? 0, rgba[idx + 2] ?? 0));
    }
  }
  if (samples.length === 0) return { contrastRatio: 1, darkLum: 0.5, lightLum: 0.5 };
  samples.sort((a, b) => a - b);
  const darkLum = samples[Math.min(samples.length - 1, Math.floor(samples.length * 0.15))] ?? 0;
  const lightLum = samples[Math.min(samples.length - 1, Math.floor(samples.length * 0.85))] ?? 1;
  const contrastRatio = Number(
    ((Math.max(darkLum, lightLum) + 0.05) / (Math.min(darkLum, lightLum) + 0.05)).toFixed(2),
  );
  return { contrastRatio, darkLum, lightLum };
}

function checkQuietZoneClear(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  box: QrBoundingBox,
  moduleDimension: number,
  darkLum: number,
  lightLum: number,
): boolean {
  const modulePx = Math.max(1, Math.min(box.widthPx, box.heightPx) / Math.max(21, moduleDimension));
  const reqMargin = Math.max(2, Math.floor(modulePx * 3.2));
  if (
    box.minX < reqMargin ||
    box.minY < reqMargin ||
    width - 1 - box.maxX < reqMargin ||
    height - 1 - box.maxY < reqMargin
  ) {
    return false;
  }
  const midThreshold = darkLum + (lightLum - darkLum) * 0.55;
  const pad = Math.max(1, Math.ceil(modulePx * 0.75));
  let ring = 0;
  let darkRing = 0;
  for (
    let y = Math.max(0, Math.floor(box.minY - reqMargin));
    y <= Math.min(height - 1, Math.ceil(box.maxY + reqMargin));
    y += 1
  ) {
    for (
      let x = Math.max(0, Math.floor(box.minX - reqMargin));
      x <= Math.min(width - 1, Math.ceil(box.maxX + reqMargin));
      x += 1
    ) {
      if (x >= box.minX - pad && x <= box.maxX + pad && y >= box.minY - pad && y <= box.maxY + pad)
        continue;
      ring += 1;
      const idx = (y * width + x) * 4;
      if (
        srgbToRelativeLuminance(rgba[idx] ?? 0, rgba[idx + 1] ?? 0, rgba[idx + 2] ?? 0) <
        midThreshold
      )
        darkRing += 1;
    }
  }
  return ring > 0 && darkRing / ring <= 0.08;
}

export function analyzeQrCodeFrame(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
): QrFrameAnalysis {
  const empty: QrFrameAnalysis = {
    decoded: false,
    decodedLink: null,
    version: null,
    boundingBox: null,
    sizeRatio: null,
    contrastRatio: null,
    quietZoneClear: null,
    warnings: [],
  };
  if (width <= 0 || height <= 0 || rgba.length !== width * height * 4) return empty;
  let qr: QRCode | null = null;
  try {
    qr = runJsQrFull(rgba, width, height);
  } catch {
    qr = null;
  }
  if (!qr || qr.data.trim().length === 0) return empty;

  const box = computeBoundingBox(qr);
  const qrMinDim = Math.min(box.widthPx, box.heightPx);
  const sizeRatio = Number((qrMinDim / Math.max(1, Math.min(width, height))).toFixed(3));
  const moduleDim = qr.version > 0 ? qr.version * 4 + 17 : 29;
  const { contrastRatio, darkLum, lightLum } = measureBoxContrast(rgba, width, height, box);
  const quietZoneClear = checkQuietZoneClear(
    rgba,
    width,
    height,
    box,
    moduleDim,
    darkLum,
    lightLum,
  );

  const warnings: QrRoughWarning[] = [];
  if (!quietZoneClear) warnings.push(ROUGH_WARNING_METADATA.MISSING_QUIET_ZONE);
  if (qrMinDim < 72 || sizeRatio < 0.18) warnings.push(ROUGH_WARNING_METADATA.TINY_SIZE);
  if (contrastRatio < 4.5 || lightLum <= darkLum)
    warnings.push(ROUGH_WARNING_METADATA.LOW_CONTRAST);

  return {
    decoded: true,
    decodedLink: qr.data.trim(),
    version: qr.version,
    boundingBox: box,
    sizeRatio,
    contrastRatio,
    quietZoneClear,
    warnings,
  };
}

export function normalizeComparableUrl(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  if (trimmed.length === 0) return '';
  try {
    const p = new URL(trimmed);
    return `${p.protocol.toLowerCase()}//${p.host.toLowerCase()}${p.pathname.replace(/\/+$/, '') || '/'}${p.search}`;
  } catch {
    return trimmed.replace(/\/+$/, '');
  }
}

export function buildExpectedProductQrLink(record: IdentifierRecord, host: string): string {
  return buildGs1DigitalLinkUrl({ host, ai: record.ai, primaryKey: record.primaryKey });
}

export function resolveExpectedLiveDestinationUrl(record: IdentifierRecord): string | null {
  if (isIdentifierRecalled(record)) {
    const override = record.recallOverride?.overrideUrl?.trim();
    if (override) return override;
  }
  const activeDefault = (record.draft?.defaultUrl ?? record.defaultUrl)?.trim();
  if (activeDefault) return activeDefault;
  return (
    (record.draft?.links ?? record.links ?? []).find((l) => l.defaultLink)?.href?.trim() || null
  );
}

export function findMatchingWorkspaceProduct(
  decodedLink: string,
  records: readonly IdentifierRecord[],
  host: string,
): IdentifierRecord | null {
  const norm = normalizeComparableUrl(decodedLink);
  for (const r of records) {
    if (normalizeComparableUrl(buildExpectedProductQrLink(r, host)) === norm) return r;
  }
  try {
    const [aiSeg, keySeg] = new URL(decodedLink).pathname.split('/').filter((s) => s.length > 0);
    if (aiSeg !== undefined && keySeg !== undefined) {
      const strip = (v: string): string => v.replace(/^0+/, '');
      for (const r of records) {
        if (r.ai === aiSeg && (r.primaryKey === keySeg || strip(r.primaryKey) === strip(keySeg)))
          return r;
      }
    }
  } catch {
    // Non-URL payload
  }
  return null;
}

export async function evaluateScannedQrCheck(p: {
  readonly frameAnalysis: QrFrameAnalysis;
  readonly records: readonly IdentifierRecord[];
  readonly selectedIdentifierKey?: string | null;
  readonly effectiveResolverHost: string;
  readonly probeLiveResolution?: LiveLinkResolverProbe;
}): Promise<QrCheckEvaluation> {
  const {
    frameAnalysis: fa,
    records,
    selectedIdentifierKey: selKey,
    effectiveResolverHost: host,
  } = p;
  const explicit =
    selKey && selKey !== 'AUTO' ? (records.find((r) => r.identifierKey === selKey) ?? null) : null;
  const matchedProduct =
    explicit ??
    (fa.decodedLink !== null ? findMatchingWorkspaceProduct(fa.decodedLink, records, host) : null);
  const expectedProductLink =
    matchedProduct !== null ? buildExpectedProductQrLink(matchedProduct, host) : null;
  const matchesExpectedLink =
    fa.decoded &&
    fa.decodedLink !== null &&
    expectedProductLink !== null &&
    normalizeComparableUrl(fa.decodedLink) === normalizeComparableUrl(expectedProductLink);
  const name =
    matchedProduct?.draft?.title ?? matchedProduct?.title ?? matchedProduct?.primaryKey ?? '';
  const expectedLinkSummary = !fa.decoded
    ? 'No QR code detected in the camera frame yet.'
    : matchedProduct === null
      ? 'Does not match any product in this workspace.'
      : matchesExpectedLink
        ? `Matches ${name}.`
        : `Does not match expected link for ${name}.`;
  const expectedLiveUrl =
    matchedProduct !== null ? resolveExpectedLiveDestinationUrl(matchedProduct) : null;

  let liveResolution: QrLiveResolutionCheckResult;
  if (!fa.decoded || fa.decodedLink === null) {
    liveResolution = {
      resolvesToRightLiveLink: false,
      expectedLiveUrl,
      resolvedLiveUrl: null,
      source: 'record',
      summary: 'Scan a QR code first to check its live destination.',
    };
  } else if (p.probeLiveResolution !== undefined) {
    const probed = await p.probeLiveResolution({
      decodedLink: fa.decodedLink,
      matchedProduct,
      expectedLiveUrl,
    });
    const okLive =
      probed.ok &&
      probed.resolvedUrl !== null &&
      expectedLiveUrl !== null &&
      normalizeComparableUrl(probed.resolvedUrl) === normalizeComparableUrl(expectedLiveUrl);
    liveResolution = {
      resolvesToRightLiveLink: okLive,
      expectedLiveUrl,
      resolvedLiveUrl: probed.resolvedUrl,
      source: 'http',
      summary: okLive
        ? `Resolves to the active live link (${probed.resolvedUrl}).`
        : probed.resolvedUrl === null
          ? 'Could not reach the live link destination.'
          : `Resolves to ${probed.resolvedUrl}, which does not match expected ${expectedLiveUrl ?? 'live destination'}.`,
    };
  } else {
    const valid = matchedProduct?.validation?.status !== 'INVALID';
    const okLive =
      matchesExpectedLink &&
      valid &&
      expectedLiveUrl !== null &&
      /^https?:\/\//i.test(expectedLiveUrl);
    liveResolution = {
      resolvesToRightLiveLink: okLive,
      expectedLiveUrl,
      resolvedLiveUrl: okLive ? expectedLiveUrl : null,
      source: 'record',
      summary: okLive
        ? `Resolves to the right live link (${expectedLiveUrl}).`
        : !matchesExpectedLink
          ? 'Decoded link does not match the product resolver link, so it will not route to this product.'
          : !valid
            ? 'Product has a validation issue and is not ready to resolve cleanly.'
            : 'No active live destination URL is set for this product.',
    };
  }

  return {
    frameAnalysis: fa,
    matchedProduct,
    expectedProductLink,
    matchesExpectedLink,
    expectedLinkSummary,
    liveResolution,
    verifierNotice: QR_CHECK_VERIFIER_NOTICE,
  };
}
